import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware'

import { createSelectors } from '@store/utils'
import type { OnboardingSlice } from './slices/onboarding'
import { createOnboardingSlice, initialOnboardingState } from './slices/onboarding'
import type { OrganizationSlice } from './slices/organization'
import { createOrganizationSlice, initialOrganizationState } from './slices/organization'

export interface State {}

export interface Actions {}

type Store = State &
  Actions &
  OnboardingSlice &
  OrganizationSlice & {
    reset: () => void
  }

const createState = immer<Store>((...args) => ({
  ...createOnboardingSlice(...args),
  ...createOrganizationSlice(...args),
  reset: () => args[0]({ ...initialOnboardingState, ...initialOrganizationState }),
}))

/**
 * v0 persisted the Intercom product-tour lock (`startPath` + `onboardingStep`)
 * but never persisted `isOnboarding`. On reload the pair rehydrated truthy
 * against an `isOnboarding` that had reset to `false`, which is exactly the
 * shape `Layout`'s `isLocked` tests for, so the full-viewport overlay latched
 * on permanently and swallowed every click in the app (ADN-547).
 *
 * `partialize` only governs WRITES: keys already in localStorage still merge
 * over the initial state on rehydrate. Dropping them below therefore stops new
 * writes but would leave every already-affected browser stuck forever. This
 * migration is what actually clears them.
 */
export const dropTourLockState = (persistedState: unknown): Record<string, unknown> => {
  // Whatever JSON.parse produced for a hand-edited or corrupted `store` key
  // reaches this function. Spreading a string would turn it into indexed
  // character properties, and the default rehydration merge writes those
  // straight into the store, so anything that is not a plain object is
  // discarded rather than reinterpreted.
  if (typeof persistedState !== 'object' || persistedState === null || Array.isArray(persistedState)) {
    return {}
  }

  const state = { ...(persistedState as Record<string, unknown>) }
  delete state.startPath
  delete state.onboardingStep
  return state
}

// Tour lock state is deliberately absent: it is per-session state that must not
// outlive a reload. See dropTourLockState above.
export type PersistedStore = Pick<Store, 'onboardingType' | 'organization' | 'organizationName' | 'integration'>

export const partializeStore = (state: PersistedStore): PersistedStore => ({
  onboardingType: state.onboardingType,
  organization: state.organization,
  organizationName: state.organizationName,
  integration: state.integration,
})

const persisState = persist(createState, {
  name: 'store',
  version: 1,
  migrate: dropTourLockState,
  getStorage: () => localStorage,
  partialize: partializeStore,
})

const name = 'state'
const useStoreBase = create(devtools(subscribeWithSelector(persisState), { name, store: name }))

export const useStore = createSelectors(useStoreBase)
export default useStore
